/**
 * age.js — TEST 2.25 (P5 step 16): the account holder's date of birth, judged on the device.
 *
 * ★★★ THE SERVER IS THE BACKSTOP, THIS IS THE COURTESY. From API v243.258.0 (P5 step 9)
 *   PUT /api/v1/profile refuses a minor or invalid DOB on the account holder's own profile
 *   with 400 {code: 'dob_under_18'} or {code: 'dob_invalid'}. This file lets the picker say
 *   so before the save, and lets every save handler turn either code into the same
 *   translated sentence instead of "Unknown error".
 *
 * ★★ THE SAME RULE AS THE SERVER'S _dob_adult_verdict, with one deliberate difference:
 *   `today` here is the DEVICE's local calendar date, while the server judges at UTC+14 so
 *   it never refuses what a device anywhere in the US admits. The device can therefore
 *   only be stricter by at most the same day, never more lenient.
 *   - Only ISO YYYY-MM-DD is a date here (DobPicker emits nothing else). A year before
 *     1900, an impossible day (2008-02-30) and a date after today are 'invalid'.
 *   - Feb 29: the birthday counts as reached on Mar 1 in a common year (as the server).
 *   - Nothing to judge (null, undefined, '') answers null: clearing is not refused here.
 *
 * ★ PLAIN ESM WITH NO IMPORTS, so scripts/check-dob-adult.mjs runs it in node as is.
 *
 * Scope (Alex P5 D5, a Dr. Rami item): the 18+ rule applies to the account holder's own
 * DOB only. The insurance SUBSCRIBER's DOB (ProfileInsurance, OnboardingInsurance) is a
 * different person's and is never judged by it.
 */

export const ACCOUNT_HOLDER_MIN_AGE = 18

// The earliest year the picker offers stays where it always was: 120 years back.
const PICKER_SPAN_YEARS = 121
const PICKER_DEFAULT_YEAR = 1990

function todayParts(today) {
  if (today && typeof today === 'object' && typeof today.getFullYear === 'function') {
    return { y: today.getFullYear(), m: today.getMonth() + 1, d: today.getDate() }
  }
  if (today && typeof today === 'object' && Number.isInteger(today.y)) {
    return { y: today.y, m: today.m, d: today.d }
  }
  const now = new Date()
  return { y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate() }
}

function isRealDay(y, m, d) {
  if (m < 1 || m > 12 || d < 1) return false
  const days = [31, (y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0)) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
  return d <= days[m - 1]
}

/**
 * 'ok' | 'minor' | 'invalid' | null.
 * @param {string} iso      the DOB as YYYY-MM-DD
 * @param {number} minAge   the age that must have been reached (0 = any real past date)
 * @param {Date|{y,m,d}} today  defaults to the device's local date
 */
export function ageVerdict(iso, minAge = 0, today) {
  if (iso === null || iso === undefined) return null
  if (typeof iso !== 'string') return 'invalid'
  const s = iso.trim()
  if (!s) return null
  const m = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(s)
  if (!m) return 'invalid'
  const y = +m[1], mo = +m[2], d = +m[3]
  if (!isRealDay(y, mo, d) || y < 1900) return 'invalid'
  const t = todayParts(today)
  if (y > t.y || (y === t.y && (mo > t.m || (mo === t.m && d > t.d)))) return 'invalid'
  const before = t.m < mo || (t.m === mo && t.d < d)
  const age = t.y - y - (before ? 1 : 0)
  return age >= (Number(minAge) || 0) ? 'ok' : 'minor'
}

/** The picker's year column, newest first: it starts at nowY - minAge. */
export function dobYears(nowY, minAge = 0) {
  const lead = Math.max(0, Math.floor(Number(minAge) || 0))
  const length = Math.max(1, PICKER_SPAN_YEARS - lead)
  return Array.from({ length }, (_, i) => nowY - lead - i)
}

/** Where the year column opens when there is no value: 1990, clamped into the column. */
export function dobDefaultYearIndex(years) {
  if (!Array.isArray(years) || years.length === 0) return 0
  const i = years.indexOf(PICKER_DEFAULT_YEAR)
  if (i >= 0) return i
  return years[0] < PICKER_DEFAULT_YEAR ? 0 : years.length - 1
}

/**
 * The translation key for a server refusal of the account holder's DOB, or null.
 * Accepts the thrown apiFetch value, a save result carrying `code`, or the code itself.
 */
export function dobRefusalKey(errOrCode) {
  const code = typeof errOrCode === 'string' ? errOrCode : (errOrCode && errOrCode.code)
  if (code === 'dob_under_18') return 'dob.tooYoung'
  if (code === 'dob_invalid') return 'dob.invalid'
  return null
}
